import { hasMatchMedia, hasResizeObserver, requestFrame, type FrameHandle } from '../shared/platform';

function setHeaderHeight(header: HTMLElement): void {
  const height = Math.ceil(header.getBoundingClientRect().height);
  document.documentElement.style.setProperty('--site-header-height', `${height}px`);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function interpolate(from: number, to: number, progress: number): number {
  return from + (to - from) * progress;
}

function toPixels(value: string): number {
  const trimmed = value.trim();

  if (trimmed === '') {
    return 0;
  }

  if (trimmed.endsWith('rem')) {
    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    return parseFloat(trimmed) * rootFontSize;
  }

  if (trimmed.endsWith('px')) {
    return parseFloat(trimmed);
  }

  return parseFloat(trimmed) || 0;
}

function initHeaderActionTooltips(): void {
  const actions = document.querySelectorAll<HTMLElement>('.site-header-action[data-tooltip]');

  if (actions.length === 0) {
    return;
  }

  const muteTooltip = (action: HTMLElement): void => {
    action.classList.add('is-tooltip-muted');
  };

  const unmuteTooltip = (action: HTMLElement): void => {
    if (action.getAttribute('aria-expanded') === 'true') {
      return;
    }

    action.classList.remove('is-tooltip-muted');
  };

  actions.forEach((action) => {
    action.addEventListener('pointerdown', () => {
      muteTooltip(action);
    });

    action.addEventListener('click', () => {
      muteTooltip(action);
    });

    action.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        muteTooltip(action);
      }
    });

    action.addEventListener('pointerleave', () => {
      unmuteTooltip(action);
    });

    action.addEventListener('blur', () => {
      unmuteTooltip(action);
    });

    if (action.dataset.bsToggle === 'dropdown') {
      action.addEventListener('hidden.bs.dropdown', () => {
        action.classList.remove('is-tooltip-muted');
      });

      action.addEventListener('shown.bs.dropdown', () => {
        muteTooltip(action);
      });
    }

    if (action.dataset.bsToggle === 'offcanvas') {
      const targetSelector = action.getAttribute('data-bs-target');
      const offcanvas = targetSelector ? document.querySelector<HTMLElement>(targetSelector) : null;

      if (!offcanvas) {
        return;
      }

      offcanvas.addEventListener('shown.bs.offcanvas', () => {
        muteTooltip(action);
      });

      offcanvas.addEventListener('hidden.bs.offcanvas', () => {
        action.classList.remove('is-tooltip-muted');
      });
    }
  });
}

export function initSiteHeader(): void {
  const header = document.querySelector<HTMLElement>('[data-site-header]');
  const metaBar = document.querySelector<HTMLElement>('[data-announcement-bar]');
  const brandSlot = header?.querySelector<HTMLElement>('.site-header__brand-slot');

  if (!header || !brandSlot) {
    return;
  }

  const desktopMediaQuery = hasMatchMedia()
    ? window.matchMedia('(min-width: 992px)')
    : null;

  let rafId: FrameHandle | null = null;
  let isDesktopScrollBound = false;

  const applyLogoState = (progress: number): void => {
    const rootStyles = getComputedStyle(document.documentElement);
    const slotWidth = brandSlot.getBoundingClientRect().width;
    const initialScale = parseFloat(rootStyles.getPropertyValue('--site-logo-initial-scale')) || 1;
    const stickyScale = parseFloat(rootStyles.getPropertyValue('--site-logo-sticky-scale')) || 1;
    const stickyShift = toPixels(rootStyles.getPropertyValue('--site-logo-sticky-shift'));
    const renderWidth = Math.round(interpolate(slotWidth * initialScale, slotWidth * stickyScale, progress));
    const translateY = Math.round(interpolate(0, stickyShift, progress));

    header.style.setProperty('--site-logo-scroll-progress', progress.toFixed(4));
    header.style.setProperty('--site-logo-render-width', `${renderWidth}px`);
    header.style.setProperty('--site-logo-translate-y', `${translateY}px`);
    header.classList.toggle('is-stuck', progress >= 0.999);
  };

  const updateLogoProgress = (): void => {
    if (desktopMediaQuery !== null && !desktopMediaQuery.matches) {
      applyLogoState(1);
      return;
    }

    const transitionDistance = Math.max(metaBar?.getBoundingClientRect().height ?? 0, 1);
    const scrollTop = window.scrollY || window.pageYOffset;
    const progress = clamp(scrollTop / transitionDistance, 0, 1);
    applyLogoState(progress);
  };

  const syncHeaderState = (): void => {
    rafId = null;
    updateLogoProgress();
    setHeaderHeight(header);
  };

  const requestSyncHeaderState = (): void => {
    if (rafId !== null) {
      return;
    }

    rafId = requestFrame(() => {
      syncHeaderState();
    });
  };

  const bindDesktopScroll = (): void => {
    if (isDesktopScrollBound) {
      return;
    }

    window.addEventListener('scroll', requestSyncHeaderState, { passive: true });
    isDesktopScrollBound = true;
  };

  const unbindDesktopScroll = (): void => {
    if (!isDesktopScrollBound) {
      return;
    }

    window.removeEventListener('scroll', requestSyncHeaderState);
    isDesktopScrollBound = false;
  };

  const syncViewportMode = (): void => {
    if (desktopMediaQuery === null || desktopMediaQuery.matches) {
      bindDesktopScroll();
    } else {
      unbindDesktopScroll();
    }

    syncHeaderState();
  };

  initHeaderActionTooltips();
  syncViewportMode();
  window.addEventListener('load', syncHeaderState, { once: true });

  if (hasResizeObserver()) {
    const observer = new ResizeObserver(() => {
      syncHeaderState();
    });

    observer.observe(header);

    if (metaBar) {
      observer.observe(metaBar);
    }
  }

  window.addEventListener('resize', requestSyncHeaderState, { passive: true });

  if (desktopMediaQuery !== null) {
    if (typeof desktopMediaQuery.addEventListener === 'function') {
      desktopMediaQuery.addEventListener('change', syncViewportMode);
    } else {
      desktopMediaQuery.addListener(syncViewportMode);
    }
  }
}
